import { useMemo, useState } from 'react';
import { track, useTrackScreen } from '@/lib/analytics';
import { View, Text, Pressable, ScrollView, TextInput } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import AnimatedPressable from '@/components/AnimatedPressable';
import { ScreenFrame } from '@/components/ScreenFrame';
import {
  TrendingUp,
  TrendingDown,
  Building2,
  Percent,
  Plus,
  X,
  Edit3,
  Crown,
  Check,
  LayoutGrid,
  LayoutList,
  List,
  Eye,
  EyeOff,
  Filter,
  RotateCcw,
  CheckCircle2,
  Clock,
  AlertCircle,
  } from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb, webStyle } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { AppLayout } from '@/components/AppLayout';
import { useProfileStore } from '@/lib/state/profile-store';
import type { InvestmentRecord } from '@/lib/state/profile-store';
import { useShallow } from 'zustand/react/shallow';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { ListLayoutType, useListLayout } from '@/components/ListLayout';
import { SectionDivider } from '@/components/SectionDivider';
import { SectionHeader } from '@/components/ui';
import { hapticLight, hapticMedium, hapticSuccess } from '@/lib/haptics';
import { UpgradePrompt } from '@/components/subscription';
import {
  usePortfolioFromSupabase,
  useUpsertPortfolioCompany,
  useDeletePortfolioCompany,
} from '@/lib/supabase/investor-queries';
import { useCompanyLogos } from '@/lib/supabase/company-logos';
import {
  useTrackRecord,
  useUpsertTrackRecord,
  useDeleteTrackRecord,
} from '@/lib/supabase/track-record';
import { useAuth } from '@/lib/supabase/auth';
import { handleError } from '@/lib/errors';
import { useAlert } from '@/components/AlertDialog';
import { isSupabaseConfigured } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';
import { PortfolioCard } from '@/components/investor/PortfolioCard';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';

// Form types for adding/editing investments
type InvestmentStage = 'pre-seed' | 'seed' | 'bridge' | 'series-a' | 'series-b' | 'series-c+';
type InvestmentType = 'direct' | 'syndicate';
type SecurityType = 'safe' | 'convertible-note' | 'equity' | 'other';
type InvestorRole = 'none' | 'board-member' | 'board-observer' | 'advisor';

const STAGES: { value: InvestmentStage; label: string }[] = [
  { value: 'pre-seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'series-a', label: 'Series A' },
  { value: 'series-b', label: 'Series B' },
  { value: 'series-c+', label: 'Series C+' },
];

const INVESTMENT_TYPES: { value: InvestmentType; label: string }[] = [
  { value: 'direct', label: 'Direct' },
  { value: 'syndicate', label: 'Syndicate' },
];

const SECURITY_TYPES: { value: SecurityType; label: string }[] = [
  { value: 'safe', label: 'SAFE' },
  { value: 'convertible-note', label: 'Convertible Note' },
  { value: 'equity', label: 'Equity' },
  { value: 'other', label: 'Other' },
];

const ADDITIONAL_ROLES: { value: InvestorRole; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'board-observer', label: 'Board Observer' },
  { value: 'board-member', label: 'Board Member' },
];

// Portfolio company status
type CompanyStatus = 'active' | 'exited' | 'watchlist' | 'at_risk';

const STATUS_OPTIONS: { value: CompanyStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'watchlist', label: 'Watchlist' },
  { value: 'at_risk', label: 'At Risk' },
  { value: 'exited', label: 'Exited' },
];

const INDUSTRIES = [
  'AI/ML', 'SaaS', 'FinTech', 'HealthTech', 'CleanTech', 'EdTech',
  'E-commerce', 'Marketplace', 'B2B', 'B2C', 'DeepTech', 'BioTech',
  'Cybersecurity', 'Gaming', 'Media', 'Other',
];

// Calculate IRR (simplified annual return based on holding period)
function calculateIRR(investmentDate: string, investmentAmount: number, currentValue: number): number {
  if (!investmentAmount || investmentAmount <= 0 || !currentValue) return 0;

  const startDate = new Date(investmentDate);
  const today = new Date();
  const yearsHeld = (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365);

  if (yearsHeld <= 0) return 0;

  // IRR = (Current Value / Investment)^(1/years) - 1
  const irr = (Math.pow(currentValue / investmentAmount, 1 / yearsHeld) - 1) * 100;
  return irr;
}

const getStatusConfig = (colors: ReturnType<typeof useThemeColors>): Record<CompanyStatus, { label: string; color: string; bg: string; icon: typeof CheckCircle2 }> => ({
  active: { label: 'Active', color: colors.traffic.green, bg: `${colors.traffic.green}15`, icon: CheckCircle2 },
  exited: { label: 'Exited', color: colors.primary[600], bg: colors.primary[50], icon: TrendingUp },
  watchlist: { label: 'Watchlist', color: colors.traffic.amber, bg: `${colors.traffic.amber}15`, icon: Clock },
  at_risk: { label: 'At Risk', color: colors.traffic.red, bg: `${colors.traffic.red}15`, icon: AlertCircle },
});

const FILTER_OPTIONS: { label: string; value: CompanyStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Exited', value: 'exited' },
  { label: 'Watchlist', value: 'watchlist' },
  { label: 'At Risk', value: 'at_risk' },
];

// Demo portfolio data (displayed as if entered by the user)
const DEMO_PORTFOLIO: InvestmentRecord[] = [
  {
    id: 'demo-1',
    companyName: 'TechFlow AI',
    industry: 'AI/ML',
    stage: 'seed',
    investmentType: 'direct',
    securityType: 'safe',
    date: '2023-03-15',
    investmentAmount: 50000,
    valuationAtInvestment: 8000000,
    currentValuation: 25000000,
    ownership: 0.625,
    status: 'active',
    investorRole: 'advisor',
    lastUpdate: '2024-12-15',
    metrics: {
      revenue: 45000,
      growth: 25,
      runway: 18,
      employees: 12,
    },
    source: 'manual',
  },
  {
    id: 'demo-2',
    companyName: 'GreenEnergy Solutions',
    industry: 'CleanTech',
    stage: 'series-a',
    investmentType: 'direct',
    securityType: 'equity',
    date: '2022-06-01',
    investmentAmount: 100000,
    valuationAtInvestment: 15000000,
    currentValuation: 45000000,
    ownership: 0.67,
    status: 'active',
    investorRole: 'board-member',
    lastUpdate: '2024-11-20',
    metrics: {
      revenue: 120000,
      growth: 40,
      runway: 24,
      employees: 35,
    },
    source: 'manual',
  },
  {
    id: 'demo-3',
    companyName: 'FinanceBot',
    industry: 'FinTech',
    stage: 'pre-seed',
    investmentType: 'syndicate',
    securityType: 'safe',
    date: '2024-01-10',
    investmentAmount: 25000,
    valuationAtInvestment: 4000000,
    currentValuation: 6000000,
    ownership: 0.625,
    status: 'watchlist',
    investorRole: 'none',
    lastUpdate: '2024-10-05',
    metrics: {
      revenue: 8000,
      growth: 15,
      runway: 10,
      employees: 5,
    },
    source: 'manual',
  },
  {
    id: 'demo-4',
    companyName: 'HealthTrack Pro',
    industry: 'HealthTech',
    stage: 'seed',
    investmentType: 'direct',
    securityType: 'convertible-note',
    date: '2022-09-20',
    investmentAmount: 75000,
    valuationAtInvestment: 10000000,
    currentValuation: 35000000,
    ownership: 0.75,
    status: 'active',
    investorRole: 'board-observer',
    lastUpdate: '2024-12-01',
    metrics: {
      revenue: 85000,
      growth: 30,
      runway: 20,
      employees: 22,
    },
    source: 'manual',
  },
  {
    id: 'demo-5',
    companyName: 'EduLearn Platform',
    industry: 'EdTech',
    stage: 'series-a',
    investmentType: 'direct',
    securityType: 'equity',
    date: '2021-04-15',
    investmentAmount: 150000,
    valuationAtInvestment: 20000000,
    currentValuation: 80000000,
    ownership: 0.75,
    status: 'exited',
    investorRole: 'board-member',
    lastUpdate: '2024-08-15',
    metrics: {
      revenue: 250000,
      growth: 50,
      employees: 60,
    },
    source: 'manual',
  },
  {
    id: 'demo-6',
    companyName: 'CyberShield Security',
    industry: 'Cybersecurity',
    stage: 'seed',
    investmentType: 'syndicate',
    securityType: 'safe',
    date: '2023-11-01',
    investmentAmount: 35000,
    valuationAtInvestment: 6000000,
    currentValuation: 5000000,
    ownership: 0.58,
    status: 'at_risk',
    investorRole: 'advisor',
    lastUpdate: '2024-12-10',
    metrics: {
      revenue: 15000,
      growth: -5,
      runway: 6,
      employees: 8,
    },
    source: 'manual',
  },
];

// Format valuation for display
const formatValuation = (value: number | undefined): string => {
  if (!value) return '';
  if (value >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`;
  }
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value}`;
};

export default function PortfolioScreen() {
  useTrackScreen('Portfolio');
  const colors = useThemeColors();
  const STATUS_CONFIG = getStatusConfig(colors);
  const { isDesktop } = useResponsive();
  const [filter, setFilter] = useState<CompanyStatus | 'all'>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { layout, setLayout } = useListLayout('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Hard-locked to sample mode: DEMO_PORTFOLIO must never be reachable in
  // production, so the toggle only exists (and the state can only be true)
  // when mode.showSampleData is on.
  const [showDemoData, setShowDemoData] = useState(mode.showSampleData);
  const demoDataActive = mode.showSampleData && showDemoData;
  const [isMasked, setIsMasked] = useState(true);
  const pageSize = 10;

  // Feature gating - portfolio tracking requires Premium
  const hasPortfolioTracking = useSubscriptionStore((s) => s.hasFeature('portfolio_tracking'));

  // Store data (must be called unconditionally — hooks cannot follow early returns)
  const investorProfile = useProfileStore(useShallow((s) => s.investorProfile));
  const setInvestorProfile = useProfileStore((s) => s.setInvestorProfile);

  // Real portfolio data from Supabase
  // AUTH USER ID, not investorProfile.id: portfolio_companies FKs auth.users
  // and every policy is `investor_id = auth.uid()`. Passing the profile id read
  // zero rows and had writes rejected by the INSERT WITH CHECK.
  const { user } = useAuth();
  const { error: showError } = useAlert();
  const { data: dbPortfolio = [] } = usePortfolioFromSupabase(user?.id);
  const upsertMutation = useUpsertPortfolioCompany();

  // Track record — the investor's declared past investments.
  //
  // THIS USED TO READ `investorProfile.investmentHistory`, a device-local
  // store. That is why the portfolio differed between mobile and web: each
  // platform persisted its own half and neither could see the other's. The
  // records now live in `investor_track_record`, so both platforms read the
  // same list. Holdings (`dbPortfolio`) and track record stay separate
  // concepts — see the migration for why conflating them kept masking bugs.
  const { data: trackRecord = [] } = useTrackRecord(user?.id);
  const deleteTrackRecordMutation = useDeleteTrackRecord();
  const upsertTrackRecordMutation = useUpsertTrackRecord();
  const deletePortfolioMutation = useDeletePortfolioCompany();
  const userInvestments = trackRecord;

  // Which table a row came from, so edit/delete hit the right one. The two
  // lists are merged for display but are different records with different
  // policies — holdings are entitlement-gated, track record is not.
  const trackRecordIds = useMemo(
    () => new Set(trackRecord.map((r) => r.id)),
    [trackRecord],
  );
  const isTrackRecordEntry = (id: string) => trackRecordIds.has(id);

  // Form state - includes all InvestmentRecord fields
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '' as string,
    stage: 'seed' as InvestmentStage,
    investmentType: 'direct' as InvestmentType,
    securityType: 'safe' as SecurityType,
    year: new Date().getFullYear().toString(),
    investmentAmount: '',
    valuationAtInvestment: '',
    currentValuation: '',
    ownership: '',
    status: 'active' as CompanyStatus,
    investorRole: 'none' as InvestorRole,
  });

  // Show upgrade prompt if user doesn't have portfolio tracking access
  // Logos for BOTH row kinds. Holdings hang off `portfolio_companies`, whose
  // `startup_id` has no foreign key, and track-record rows have no startup id
  // at all — so neither can use the PostgREST embed the dealflow list does.
  //
  // Declared BEFORE the entitlement early-return below: a hook after a
  // conditional return changes hook order between renders. Keyed off the raw
  // sources rather than the merged list, which is derived further down.
  const logoFor = useCompanyLogos(
    [...dbPortfolio.map((p) => p.startup.id), ...trackRecord.map((r) => r.id)],
    [...dbPortfolio.map((p) => p.startup.company.name), ...trackRecord.map((r) => r.companyName)],
  );

  if (!hasPortfolioTracking) {
    return (
      <AppLayout title="Portfolio">
        <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
          <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
            {/* UpgradePrompt fullscreen is self-contained (icon + title + subtitle
                + features + price + CTA) and fills its parent. Don't wrap it with a
                second icon/title or an items-center container — that duplicated the
                header and collapsed the card's width. */}
            <UpgradePrompt
              targetTier="premium"
              variant="fullscreen"
              headline="Unlock Portfolio Tracking"
              lockedTitle="Portfolio tracking"
              maxFeatures={4}
            />
          </SafeAreaView>
        </View>
      </AppLayout>
    );
  }

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const LAYOUT_OPTIONS = [
    { value: 'list' as ListLayoutType, icon: LayoutList },
    { value: 'grid' as ListLayoutType, icon: LayoutGrid },
    { value: 'compact' as ListLayoutType, icon: List },
  ];

  // Use real DB data when available, fall back to demo only in mock mode
  const investmentHistory = dbPortfolio.length > 0
    ? [...dbPortfolio.map((p) => ({
        id: p.id,
        companyName: p.startup?.company?.name ?? 'Unknown',
        industry: p.startup?.company?.industry ?? '',
        stage: (p.startup?.company?.stage ?? 'seed') as InvestmentStage,
        investmentType: 'direct' as InvestmentType,
        securityType: 'equity' as SecurityType,
        date: p.investmentDate ?? '',
        investmentAmount: p.investmentAmount ?? 0,
        valuationAtInvestment: 0,
        currentValuation: p.currentValuation ?? 0,
        ownership: p.equity ?? 0,
        status: (p.status ?? 'active') as CompanyStatus,
        investorRole: 'none' as const,
        lastUpdate: '',
        metrics: { revenue: 0, growth: 0, runway: 0, employees: 0 },
        source: 'manual' as const,
      })), ...userInvestments]
    : (demoDataActive ? [...DEMO_PORTFOLIO, ...userInvestments] : userInvestments);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => (currentYear - i).toString());

  const resetForm = () => {
    setFormData({
      companyName: '',
      industry: '',
      stage: 'seed',
      investmentType: 'direct',
      securityType: 'safe',
      year: new Date().getFullYear().toString(),
      investmentAmount: '',
      valuationAtInvestment: '',
      currentValuation: '',
      ownership: '',
      status: 'active',
      investorRole: 'none',
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const startEdit = (entry: InvestmentRecord) => {
    setEditingId(entry.id);
    setFormData({
      companyName: entry.companyName,
      industry: entry.industry ?? '',
      stage: entry.stage as InvestmentStage,
      investmentType: entry.investmentType,
      securityType: entry.securityType ?? 'safe',
      year: entry.date.substring(0, 4),
      investmentAmount: entry.investmentAmount?.toString() ?? '',
      valuationAtInvestment: entry.valuationAtInvestment?.toString() ?? '',
      currentValuation: entry.currentValuation?.toString() ?? '',
      ownership: entry.ownership?.toString() ?? '',
      status: entry.status ?? 'active',
      investorRole: entry.investorRole ?? 'none',
    });
    setShowAddForm(true);
  };

  const canSave = formData.companyName.trim().length > 0;

  // Check if an entry is from demo data (not editable/deletable)
  const isDemoEntry = (id: string) => id.startsWith('demo-');

  const saveEntry = async () => {
    if (!canSave || !investorProfile) return;

    const investmentAmount = formData.investmentAmount
      ? parseFloat(formData.investmentAmount)
      : undefined;
    const valuationAtInvestment = formData.valuationAtInvestment
      ? parseFloat(formData.valuationAtInvestment)
      : undefined;
    const currentValuation = formData.currentValuation
      ? parseFloat(formData.currentValuation)
      : undefined;
    const ownership = formData.ownership
      ? parseFloat(formData.ownership)
      : undefined;

    // Editing a TRACK-RECORD entry updates that table and returns — it is a
    // declared past investment, not a holding, and the holdings upsert below
    // would create a duplicate row in the wrong table.
    //
    // This branch used to write the edit into the local profile store and
    // nothing else, so an edit never reached any server and was invisible on
    // every other device.
    if (editingId && isTrackRecordEntry(editingId)) {
      if (!user?.id) return;
      try {
        await upsertTrackRecordMutation.mutateAsync({
          userId: user.id,
          record: {
            id: editingId,
            companyName: formData.companyName.trim(),
            industry: formData.industry || undefined,
            stage: formData.stage,
            investmentType: formData.investmentType,
            securityType: formData.securityType,
            date: `${formData.year}-01-01`,
            investmentAmount,
            valuationAtInvestment,
            investorRole: formData.investorRole,
            source: 'manual',
          },
        });
      } catch (err) {
        const { message } = handleError(err, { context: 'PortfolioScreen.saveTrackRecord' });
        showError('Could not save', message);
        return; // keep the form populated so the entry is not lost
      }
      hapticSuccess();
      resetForm();
      return;
    }

    // Demo rows are illustrative only.
    if (editingId && isDemoEntry(editingId)) return;

    // NOTE: no local-store write here any more. Adding a holding used to write
    // it into `investorProfile.investmentHistory` AND to `portfolio_companies`
    // — the double-write that made the list differ per device, since only the
    // local half survived on the device that typed it. The DB write below is
    // now the only one, and the query invalidation refreshes the list.

    // Persist to Supabase.
    //
    // AWAITED, and the success feedback is inside the success path. This used
    // to be a fire-and-forget `.mutate()` followed by an unconditional
    // hapticSuccess() + resetForm() — so the form cleared and buzzed "saved"
    // even when the row was rejected. In production every write WAS rejected
    // (wrong id space, and portfolio_tracking is entitlement-gated), and the UI
    // reported success every time.
    if (user?.id && isSupabaseConfigured()) {
      try {
        await upsertMutation.mutateAsync({
          userId: user.id,
          company: {
            startup_name: formData.companyName.trim(),
            investment_date: `${formData.year}-01-01`,
            investment_amount: investmentAmount ?? 0,
            equity: ownership ?? 0,
            current_valuation: currentValuation ?? 0,
            status: formData.status,
            id: editingId && !isDemoEntry(editingId) ? editingId : undefined,
          },
        });
      } catch (err) {
        const { message } = handleError(err, { context: 'PortfolioScreen.save' });
        // RLS rejects the insert when the plan lacks `portfolio_tracking`
        // (investor → Standard). Say that, rather than a raw policy error.
        const isEntitlement = /row-level security|policy|42501/i.test(String(err));
        showError(
          'Could not save',
          isEntitlement
            ? 'Portfolio tracking is available on the Standard plan and above.'
            : message,
        );
        return; // keep the form populated so the entry is not lost
      }
    }

    hapticSuccess(); // Haptic feedback on save
    resetForm();
  };

  /**
   * Delete a holding or a track-record entry.
   *
   * This used to filter a local array and write it back to the profile store —
   * it NEVER issued a delete. The row came straight back on the next fetch,
   * and on any other device it had never gone at all. Now it deletes from
   * whichever table the row actually lives in, awaits the result, and reports
   * failure instead of pretending.
   */
  const deleteEntry = async (id: string) => {
    // Demo entries cannot be deleted
    if (isDemoEntry(id) || !user?.id) return;
    hapticMedium(); // Haptic feedback on delete

    try {
      if (isTrackRecordEntry(id)) {
        await deleteTrackRecordMutation.mutateAsync({ userId: user.id, id });
      } else {
        await deletePortfolioMutation.mutateAsync({ userId: user.id, id });
      }
    } catch (err) {
      const { message } = handleError(err, { context: 'PortfolioScreen.deleteEntry' });
      showError('Could not delete', message);
      return;
    }

    if (editingId === id) {
      resetForm();
    }
  };

  // Filter portfolio by status
  const filteredPortfolio = filter === 'all'
    ? investmentHistory
    : investmentHistory.filter((c) => c.status === filter);

  // Calculate portfolio summary from investmentHistory
  const totalInvested = investmentHistory.reduce((sum, c) => sum + (c.investmentAmount ?? 0), 0);
  const totalValue = investmentHistory.reduce((sum, c) => {
    const valuation = c.currentValuation ?? c.valuationAtInvestment ?? 0;
    const ownershipPct = c.ownership ?? 0;
    return sum + (valuation * ownershipPct / 100);
  }, 0);
  const totalReturn = totalInvested > 0 ? ((totalValue - totalInvested) / totalInvested) * 100 : 0;
  const activeCompanies = investmentHistory.filter((c) => c.status === 'active' || !c.status).length;

  // Calculate weighted average IRR
  const totalCurrentValue = investmentHistory.reduce((sum, c) => {
    const valuation = c.currentValuation ?? c.valuationAtInvestment ?? 0;
    const ownershipPct = c.ownership ?? 0;
    return sum + (valuation * ownershipPct / 100);
  }, 0);
  const weightedIRR = totalCurrentValue > 0 ? investmentHistory.reduce((sum, c) => {
    const valuation = c.currentValuation ?? c.valuationAtInvestment ?? 0;
    const ownershipPct = c.ownership ?? 0;
    const currentValue = valuation * ownershipPct / 100;
    if (currentValue === 0 || !c.investmentAmount) return sum;
    const weight = currentValue / totalCurrentValue;
    const irr = calculateIRR(c.date, c.investmentAmount, currentValue);
    return sum + (irr * weight);
  }, 0) : 0;

  return (
    <AppLayout title="Portfolio">
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* This page had NO page header of any kind on mobile — no title, no
           controls — so with Profile off the tab bar there was no route to the
           account from it at all. No bottomEdge: tab screen. */}
        <ScreenFrame title="Portfolio" actions={<ScreenFrame.GlobalActions />}>
          <KeyboardAwareBody className="flex-1" showsVerticalScrollIndicator={false}>
            <View className={`px-5 pb-8 ${isWeb ? 'pt-6' : 'pt-1'}`}>
              {/* Portfolio Summary */}
              <Animated.View entering={FadeInDown.delay(100).duration(500)} className="mb-6">
                <View
                  className="rounded-2xl p-5"
                  style={{
                    backgroundColor: colors.primary[600],
                  }}
                >
                  <View className="flex-row items-center justify-between mb-1">
                    <Text className="text-white/80 text-sm font-medium">
                      Portfolio Value
                    </Text>
                    <Pressable
                      onPress={() => { hapticLight(); setIsMasked(!isMasked); }}
                      accessibilityRole="switch"
                      accessibilityLabel={isMasked ? 'Show financial values' : 'Hide financial values'}
                      accessibilityState={{ checked: !isMasked }}
                      className="flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg active:opacity-70"
                      style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                      hitSlop={8}
                    >
                      {isMasked ? (
                        <EyeOff size={14} color="rgba(255,255,255,0.7)" />
                      ) : (
                        <Eye size={14} color="rgba(255,255,255,0.9)" />
                      )}
                      <Text className="text-white/70 text-xs font-medium">
                        {isMasked ? 'Show' : 'Hide'}
                      </Text>
                    </Pressable>
                  </View>
                  <Text className="text-white text-3xl font-bold mb-4">
                    {isMasked ? '••••••' : `$${(totalValue / 1000000).toFixed(2)}M`}
                  </Text>

                  <View className="flex-row gap-3 flex-wrap">
                    <View className="flex-1 bg-white/10 rounded-xl p-3" style={{ minWidth: 100 }}>
                      <Text className="text-white/70 text-xs mb-1">Total Invested</Text>
                      <Text className="text-white font-semibold">
                        {isMasked ? '••••••' : `$${(totalInvested / 1000000).toFixed(2)}M`}
                      </Text>
                    </View>
                    <View className="flex-1 bg-white/10 rounded-xl p-3" style={{ minWidth: 100 }}>
                      <Text className="text-white/70 text-xs mb-1">Total Return</Text>
                      <View className="flex-row items-center gap-1">
                        {!isMasked && (totalReturn >= 0 ? (
                          <TrendingUp size={14} color="#4ade80" />
                        ) : (
                          <TrendingDown size={14} color="#f87171" />
                        ))}
                        <Text className={isMasked ? 'text-white/50 font-semibold' : totalReturn >= 0 ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}>
                          {isMasked ? '••••' : `${totalReturn >= 0 ? '+' : ''}${totalReturn.toFixed(1)}%`}
                        </Text>
                      </View>
                    </View>
                    <View className="flex-1 bg-white/10 rounded-xl p-3" style={{ minWidth: 100 }}>
                      <Text className="text-white/70 text-xs mb-1">Portfolio IRR</Text>
                      <View className="flex-row items-center gap-1">
                        {!isMasked && <Percent size={14} color="#4ade80" />}
                        <Text className={isMasked ? 'text-white/50 font-semibold' : 'text-green-400 font-semibold'}>
                          {isMasked ? '••••' : `${weightedIRR.toFixed(1)}%`}
                        </Text>
                      </View>
                    </View>
                    <View className="flex-1 bg-white/10 rounded-xl p-3" style={{ minWidth: 100 }}>
                      <Text className="text-white/70 text-xs mb-1">Companies</Text>
                      <Text className="text-white font-semibold">
                        {activeCompanies} Active
                      </Text>
                    </View>
                  </View>
                </View>
              </Animated.View>

              {/* Section Divider - Filter */}
              <SectionDivider label="Filter" marginTop={16} marginBottom={12} />

              {/* Filter Chips */}
              <Animated.View entering={FadeInDown.delay(200).duration(500)} className="mb-4">
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <View className="flex-row gap-2">
                    {FILTER_OPTIONS.map((option) => {
                      const isActive = filter === option.value;
                      return (
                        <AnimatedPressable
                          key={option.value}
                          onPress={() => setFilter(option.value)}
                          accessibilityRole="tab"
                          accessibilityLabel={`Filter by ${option.label}`}
                          accessibilityState={{ selected: isActive }}
                          className="px-4 py-2 rounded-full"
                          style={{
                            backgroundColor: isActive ? colors.primary[600] : colors.surface.primary,
                            borderWidth: 1,
                            borderColor: isActive ? colors.primary[600] : colors.border.light,
                          }}
                        >
                          <Text
                            style={{ color: isActive ? 'white' : colors.text.secondary }}
                            className="text-sm font-medium"
                          >
                            {option.label}
                          </Text>
                        </AnimatedPressable>
                      );
                    })}
                  </View>
                </ScrollView>
              </Animated.View>

              {/* Add/Edit Form */}
              {(showAddForm || investmentHistory.length === 0) && (
                <Animated.View entering={FadeInDown.delay(350).duration(500)} className="mb-6">
                  <View
                    className="p-4 rounded-xl"
                    style={{
                      backgroundColor: colors.surface.primary,
                      borderWidth: 1,
                      borderColor: colors.primary[200],
                    }}
                  >
                    <View className="flex-row items-center justify-between mb-4">
                      <View className="flex-row items-center gap-2">
                        {editingId ? <Edit3 size={18} color={colors.primary[600]} /> : <Plus size={18} color={colors.primary[600]} />}
                        <Text style={{ color: colors.text.primary }} className="font-semibold">
                          {editingId ? 'Edit Company' : 'Add Company'}
                        </Text>
                      </View>
                      {(showAddForm && investmentHistory.length > 0) && (
                        <Pressable
                          onPress={resetForm}
                          className="flex-row items-center gap-1 px-3 py-1.5 rounded-lg"
                          style={{ backgroundColor: colors.surface.secondary }}
                        >
                          <X size={14} color={colors.text.secondary} />
                          <Text style={{ color: colors.text.secondary }} className="text-xs font-medium">
                            Cancel
                          </Text>
                        </Pressable>
                      )}
                    </View>

                    {/* Company Name + Industry */}
                    <View className="flex-row gap-2 mb-3">
                      <View className="flex-1">
                        <TextInput
                          value={formData.companyName}
                          onChangeText={(text) => setFormData({ ...formData, companyName: text })}
                          placeholder="Company name *"
                          placeholderTextColor={colors.text.tertiary}
                          className="px-4 py-3 rounded-xl text-base"
                          style={{ backgroundColor: colors.surface.secondary, color: colors.text.primary }}
                        />
                      </View>
                    </View>

                    {/* Industry */}
                    <View className="mb-3">
                      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
                        <View className="flex-row gap-2">
                          {INDUSTRIES.map((ind) => (
                            <Pressable
                              key={ind}
                              onPress={() => setFormData({ ...formData, industry: ind })}
                              className="px-3 py-2 rounded-full"
                              style={{
                                backgroundColor: formData.industry === ind ? colors.primary[600] : colors.surface.secondary,
                              }}
                            >
                              <Text
                                style={{ color: formData.industry === ind ? 'white' : colors.text.secondary }}
                                className="text-xs font-medium"
                              >
                                {ind}
                              </Text>
                            </Pressable>
                          ))}
                        </View>
                      </ScrollView>
                    </View>

                    {/* Stage */}
                    <View className="mb-3">
                      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
                        <View className="flex-row gap-2">
                          {STAGES.map((stage) => (
                            <Pressable
                              key={stage.value}
                              onPress={() => setFormData({ ...formData, stage: stage.value })}
                              className="px-3 py-2 rounded-full"
                              style={{
                                backgroundColor: formData.stage === stage.value ? colors.primary[600] : colors.surface.secondary,
                              }}
                            >
                              <Text
                                style={{ color: formData.stage === stage.value ? 'white' : colors.text.secondary }}
                                className="text-xs font-medium"
                              >
                                {stage.label}
                              </Text>
                            </Pressable>
                          ))}
                        </View>
                      </ScrollView>
                    </View>

                    {/* Type + Year */}
                    <View className="flex-row gap-2 mb-3">
                      <View className="flex-1 flex-row gap-1">
                        {INVESTMENT_TYPES.map((type) => (
                          <Pressable
                            key={type.value}
                            onPress={() => setFormData({ ...formData, investmentType: type.value })}
                            className="flex-1 py-2 rounded-lg items-center"
                            style={{
                              backgroundColor: formData.investmentType === type.value ? colors.primary[600] : colors.surface.secondary,
                            }}
                          >
                            <Text
                              style={{ color: formData.investmentType === type.value ? 'white' : colors.text.secondary }}
                              className="text-xs font-medium"
                            >
                              {type.label}
                            </Text>
                          </Pressable>
                        ))}
                      </View>
                      <View className="flex-1">
                        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
                          <View className="flex-row gap-1">
                            {years.slice(0, 8).map((year) => (
                              <Pressable
                                key={year}
                                onPress={() => setFormData({ ...formData, year })}
                                className="px-3 py-2 rounded-lg"
                                style={{
                                  backgroundColor: formData.year === year ? colors.primary[600] : colors.surface.secondary,
                                }}
                              >
                                <Text
                                  style={{ color: formData.year === year ? 'white' : colors.text.secondary }}
                                  className="text-xs font-medium"
                                >
                                  {year}
                                </Text>
                              </Pressable>
                            ))}
                          </View>
                        </ScrollView>
                      </View>
                    </View>

                    {/* Investment Amount + Valuation at Investment */}
                    <View className="flex-row gap-2 mb-3">
                      <View className="flex-1">
                        <View
                          className="flex-row items-center rounded-xl overflow-hidden"
                          style={{ backgroundColor: colors.surface.secondary }}
                        >
                          <View className="px-3 py-3">
                            <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">$</Text>
                          </View>
                          <TextInput
                            value={formData.investmentAmount}
                            onChangeText={(text) => setFormData({ ...formData, investmentAmount: text.replace(/[^0-9.]/g, '') })}
                            placeholder="Amount invested"
                            placeholderTextColor={colors.text.tertiary}
                            keyboardType="numeric"
                            className="flex-1 py-3 pr-3 text-sm"
                            style={{ color: colors.text.primary }}
                          />
                        </View>
                      </View>
                      <View className="flex-1">
                        <View
                          className="flex-row items-center rounded-xl overflow-hidden"
                          style={{ backgroundColor: colors.surface.secondary }}
                        >
                          <View className="px-3 py-3">
                            <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">$</Text>
                          </View>
                          <TextInput
                            value={formData.valuationAtInvestment}
                            onChangeText={(text) => setFormData({ ...formData, valuationAtInvestment: text.replace(/[^0-9.]/g, '') })}
                            placeholder="Entry valuation"
                            placeholderTextColor={colors.text.tertiary}
                            keyboardType="numeric"
                            className="flex-1 py-3 pr-3 text-sm"
                            style={{ color: colors.text.primary }}
                          />
                        </View>
                      </View>
                    </View>

                    {/* Current Valuation + Ownership */}
                    <View className="flex-row gap-2 mb-3">
                      <View className="flex-1">
                        <View
                          className="flex-row items-center rounded-xl overflow-hidden"
                          style={{ backgroundColor: colors.surface.secondary }}
                        >
                          <View className="px-3 py-3">
                            <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">$</Text>
                          </View>
                          <TextInput
                            value={formData.currentValuation}
                            onChangeText={(text) => setFormData({ ...formData, currentValuation: text.replace(/[^0-9.]/g, '') })}
                            placeholder="Current valuation"
                            placeholderTextColor={colors.text.tertiary}
                            keyboardType="numeric"
                            className="flex-1 py-3 pr-3 text-sm"
                            style={{ color: colors.text.primary }}
                          />
                        </View>
                      </View>
                      <View className="flex-1">
                        <View
                          className="flex-row items-center rounded-xl overflow-hidden"
                          style={{ backgroundColor: colors.surface.secondary }}
                        >
                          <TextInput
                            value={formData.ownership}
                            onChangeText={(text) => setFormData({ ...formData, ownership: text.replace(/[^0-9.]/g, '') })}
                            placeholder="Ownership %"
                            placeholderTextColor={colors.text.tertiary}
                            keyboardType="numeric"
                            className="flex-1 py-3 px-3 text-sm"
                            style={{ color: colors.text.primary }}
                          />
                          <View className="px-3 py-3">
                            <Percent size={14} color={colors.text.tertiary} />
                          </View>
                        </View>
                      </View>
                    </View>

                    {/* Status */}
                    <View className="mb-3">
                      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
                        <View className="flex-row gap-2">
                          {STATUS_OPTIONS.map((statusOpt) => {
                            const config = STATUS_CONFIG[statusOpt.value];
                            return (
                              <Pressable
                                key={statusOpt.value}
                                onPress={() => setFormData({ ...formData, status: statusOpt.value })}
                                className="px-3 py-2 rounded-lg flex-row items-center gap-1"
                                style={{
                                  backgroundColor: formData.status === statusOpt.value ? config.bg : colors.surface.secondary,
                                  borderWidth: formData.status === statusOpt.value ? 1 : 0,
                                  borderColor: config.color,
                                }}
                              >
                                <Text
                                  style={{ color: formData.status === statusOpt.value ? config.color : colors.text.secondary }}
                                  className="text-xs font-medium"
                                >
                                  {statusOpt.label}
                                </Text>
                              </Pressable>
                            );
                          })}
                        </View>
                      </ScrollView>
                    </View>

                    {/* Security Type */}
                    <View className="mb-3">
                      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }}>
                        <View className="flex-row gap-2">
                          {SECURITY_TYPES.map((security) => (
                            <Pressable
                              key={security.value}
                              onPress={() => setFormData({ ...formData, securityType: security.value })}
                              className="px-3 py-2 rounded-lg"
                              style={{
                                backgroundColor: formData.securityType === security.value ? colors.primary[600] : colors.surface.secondary,
                              }}
                            >
                              <Text
                                style={{ color: formData.securityType === security.value ? 'white' : colors.text.secondary }}
                                className="text-xs font-medium"
                              >
                                {security.label}
                              </Text>
                            </Pressable>
                          ))}
                        </View>
                      </ScrollView>
                    </View>

                    {/* Additional Role */}
                    <View className="mb-4">
                      <View className="flex-row flex-wrap gap-2">
                        {ADDITIONAL_ROLES.map((role) => (
                          <Pressable
                            key={role.value}
                            onPress={() => setFormData({ ...formData, investorRole: role.value })}
                            className="px-3 py-2 rounded-lg flex-row items-center gap-1"
                            style={{
                              backgroundColor:
                                formData.investorRole === role.value
                                  ? role.value === 'board-member' ? colors.amber.main : colors.primary[600]
                                  : colors.surface.secondary,
                            }}
                          >
                            {role.value === 'board-member' && (
                              <Crown size={12} color={formData.investorRole === role.value ? 'white' : colors.amber.main} />
                            )}
                            <Text
                              style={{ color: formData.investorRole === role.value ? 'white' : colors.text.secondary }}
                              className="text-xs font-medium"
                            >
                              {role.label}
                            </Text>
                          </Pressable>
                        ))}
                      </View>
                    </View>

                    {/* Save Button */}
                    <AnimatedPressable
                      onPress={saveEntry}
                      disabled={!canSave}
                      accessibilityRole="button"
                      accessibilityLabel={editingId ? 'Update investment entry' : 'Add to portfolio'}
                      accessibilityState={{ disabled: !canSave }}
                      className="py-3 rounded-xl items-center flex-row justify-center gap-2"
                      style={{ backgroundColor: canSave ? colors.primary[600] : colors.border.light }}
                    >
                      {editingId ? <Check size={18} color={canSave ? 'white' : colors.text.tertiary} /> : <Plus size={18} color={canSave ? 'white' : colors.text.tertiary} />}
                      <Text style={{ color: canSave ? 'white' : colors.text.tertiary }} className="font-semibold">
                        {editingId ? 'Update Entry' : 'Add to Portfolio'}
                      </Text>
                    </AnimatedPressable>
                  </View>
                </Animated.View>
              )}

              {/* Section Divider - Companies */}
              <SectionDivider label="Companies" marginTop={16} marginBottom={12} />

              {/* Portfolio Companies List */}
              {filteredPortfolio.length > 0 && (
                <Animated.View entering={FadeInDown.delay(400).duration(500)}>
                  <SectionHeader
                    title={`Portfolio Companies (${filteredPortfolio.length})`}
                    className="mb-3"
                    right={
                      <View className="flex-row items-center gap-2">
                        {/* Demo Data Toggle — sample mode only, never rendered in production */}
                        {mode.showSampleData && (
                          <Pressable
                            onPress={() => {
                              hapticLight();
                              setShowDemoData(!showDemoData);
                            }}
                            accessibilityRole="switch"
                            accessibilityLabel="Toggle demo data"
                            accessibilityState={{ checked: showDemoData }}
                            className="flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg active:opacity-70"
                            style={{
                              backgroundColor: showDemoData ? colors.surface.secondary : colors.primary[50],
                              borderWidth: 1,
                              borderColor: showDemoData ? colors.border.light : colors.primary[200],
                            }}
                          >
                            {showDemoData ? (
                              <EyeOff size={13} color={colors.text.tertiary} />
                            ) : (
                              <Eye size={13} color={colors.primary[600]} />
                            )}
                            <Text
                              style={{ color: showDemoData ? colors.text.tertiary : colors.primary[600] }}
                              className="font-medium text-xs"
                            >
                              {showDemoData ? 'Hide Demo' : 'Show Demo'}
                            </Text>
                          </Pressable>
                        )}
                        {/* Layout Toggle with Icons */}
                        <View className="flex-row items-center gap-0.5 p-1 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                          {LAYOUT_OPTIONS.map((opt) => {
                            const Icon = opt.icon;
                            const isActive = layout === opt.value;
                            return (
                              <Pressable
                                key={opt.value}
                                onPress={() => { setLayout(opt.value); setCurrentPage(1); }}
                                className="p-2 rounded"
                                style={{
                                  backgroundColor: isActive ? colors.surface.primary : 'transparent',
                                }}
                                accessibilityRole="button"
                                accessibilityLabel={`${opt.value} layout`}
                                accessibilityState={{ selected: isActive }}
                              >
                                <Icon
                                  size={18}
                                  color={isActive ? colors.primary[600] : colors.text.tertiary}
                                />
                              </Pressable>
                            );
                          })}
                        </View>
                        {/* Add Button */}
                        <AnimatedPressable
                          onPress={() => setShowAddForm(!showAddForm)}
                          accessibilityRole="button"
                          accessibilityLabel="Add new investment"
                          className="flex-row items-center gap-1 px-3 py-2 rounded-lg"
                          style={{ backgroundColor: colors.primary[600] }}
                        >
                          <Plus size={16} color="white" />
                          <Text className="text-white font-medium text-sm">Add</Text>
                        </AnimatedPressable>
                      </View>
                    }
                  />

                  {/* Sample-data banner — every figure below is demo, not real */}
                  {demoDataActive && dbPortfolio.length === 0 && (
                    <View
                      className="flex-row items-center gap-2 px-3 py-2 rounded-lg mb-3"
                      style={{ backgroundColor: colors.primary[50], borderWidth: 1, borderColor: colors.primary[200] }}
                    >
                      <Eye size={14} color={colors.primary[600]} />
                      <Text style={{ color: colors.primary[600] }} className="text-xs font-medium flex-1" numberOfLines={2}>
                        Showing sample portfolio data. Figures are illustrative — add your own investments to replace them.
                      </Text>
                    </View>
                  )}

                  {/* Paginated List */}
                  {(() => {
                    const totalPages = Math.ceil(filteredPortfolio.length / pageSize);
                    const paginatedItems = filteredPortfolio.slice(
                      (currentPage - 1) * pageSize,
                      currentPage * pageSize
                    );

                    return (
                      <>
                        <View className={layout === 'grid' && isDesktop ? 'flex-row flex-wrap gap-3' : 'gap-3'}>
                          {paginatedItems.map((company, index) => (
                            <View
                              key={company.id}
                              style={layout === 'grid' && isDesktop ? webStyle({}, { width: 'calc(50% - 6px)' }) : {}}
                            >
                              <PortfolioCard
                                company={company}
                                logoUrl={logoFor(company.companyName, company.id)}
                                index={index}
                                onEdit={() => startEdit(company)}
                                onDelete={() => deleteEntry(company.id)}
                                isDemo={isDemoEntry(company.id)}
                                layout={layout}
                                isExpanded={expandedIds.has(company.id)}
                                onToggleExpand={() => toggleExpanded(company.id)}
                                isMasked={isMasked}
                              />
                            </View>
                          ))}
                        </View>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <View className="flex-row items-center justify-center gap-2 mt-6 pt-4 border-t" style={{ borderColor: colors.border.light }}>
                            <Pressable
                              onPress={() => setCurrentPage(Math.max(1, currentPage - 1))}
                              disabled={currentPage === 1}
                              className="px-3 py-2 rounded-lg"
                              style={{
                                backgroundColor: colors.surface.primary,
                                borderWidth: 1,
                                borderColor: colors.border.light,
                                opacity: currentPage === 1 ? 0.5 : 1,
                              }}
                            >
                              <Text style={{ color: colors.text.secondary }} className="text-sm">Prev</Text>
                            </Pressable>
                            <Text style={{ color: colors.text.secondary }} className="text-sm px-3">
                              Page {currentPage} of {totalPages}
                            </Text>
                            <Pressable
                              onPress={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                              disabled={currentPage === totalPages}
                              className="px-3 py-2 rounded-lg"
                              style={{
                                backgroundColor: colors.surface.primary,
                                borderWidth: 1,
                                borderColor: colors.border.light,
                                opacity: currentPage === totalPages ? 0.5 : 1,
                              }}
                            >
                              <Text style={{ color: colors.text.secondary }} className="text-sm">Next</Text>
                            </Pressable>
                          </View>
                        )}
                      </>
                    );
                  })()}
                </Animated.View>
              )}

              {/* Filtered Empty State - Filter returned no results */}
              {filteredPortfolio.length === 0 && investmentHistory.length > 0 && (
                <Animated.View entering={FadeInDown.delay(400).duration(500)} className="items-center py-8 px-4">
                  <View
                    className="w-16 h-16 rounded-full items-center justify-center mb-3"
                    style={{ backgroundColor: colors.surface.secondary }}
                  >
                    <Filter size={28} color={colors.text.tertiary} />
                  </View>
                  <Text style={{ color: colors.text.primary }} className="font-semibold text-center mb-1">
                    No {filter !== 'all' ? FILTER_OPTIONS.find(f => f.value === filter)?.label.toLowerCase() : ''} companies
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-center text-sm mb-4">
                    {filter === 'all'
                      ? 'No portfolio companies match your criteria'
                      : `You don't have any ${FILTER_OPTIONS.find(f => f.value === filter)?.label.toLowerCase()} investments`}
                  </Text>

                  {/* Suggestions */}
                  <View className="flex-row flex-wrap justify-center gap-2">
                    {filter !== 'all' && (
                      <Pressable
                        onPress={() => setFilter('all')}
                        className="flex-row items-center gap-1.5 px-3 py-2 rounded-lg"
                        style={{ backgroundColor: colors.primary[50], borderWidth: 1, borderColor: colors.primary[200] }}
                      >
                        <RotateCcw size={14} color={colors.primary[600]} />
                        <Text style={{ color: colors.primary[600] }} className="text-sm">Show all companies</Text>
                      </Pressable>
                    )}
                    <Pressable
                      onPress={() => setShowAddForm(true)}
                      className="flex-row items-center gap-1.5 px-3 py-2 rounded-lg"
                      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                    >
                      <Plus size={14} color={colors.text.secondary} />
                      <Text style={{ color: colors.text.secondary }} className="text-sm">Add investment</Text>
                    </Pressable>
                  </View>

                  <Text style={{ color: colors.text.tertiary }} className="text-xs mt-4">
                    {investmentHistory.length} compan{investmentHistory.length !== 1 ? 'ies' : 'y'} in portfolio
                  </Text>
                </Animated.View>
              )}

              {/* Empty State */}
              {investmentHistory.length === 0 && !showAddForm && (
                <Animated.View entering={FadeInDown.delay(400).duration(500)} className="items-center py-12">
                  <Building2 size={48} color={colors.text.tertiary} />
                  <Text style={{ color: colors.text.secondary }} className="mt-4 text-center font-medium">
                    No portfolio companies yet
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="mt-2 text-center text-sm">
                    Add your first investment to start tracking
                  </Text>
                  <Pressable
                    onPress={() => setShowAddForm(true)}
                    className="flex-row items-center gap-2 px-4 py-3 rounded-xl mt-4 active:opacity-70"
                    style={{ backgroundColor: colors.primary[600] }}
                  >
                    <Plus size={18} color="white" />
                    <Text className="text-white font-semibold">Add Company</Text>
                  </Pressable>
                </Animated.View>
              )}
            </View>
          </KeyboardAwareBody>
        </ScreenFrame>
      </View>
    </AppLayout>
  );
}
